import React, { useState, useEffect, useRef } from "react";
import Loading from "../Modals/Loading";
import warehouseService from "../Services/warehouseService";
import PurchaseDetailsModal from "../Components/PurchaseDetailsModal";
import SalesDetailsModal from "../Components/SalesDetailsModal";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  MdKeyboardDoubleArrowDown,
  MdKeyboardDoubleArrowUp,
} from "react-icons/md";
import DateRangePicker from "../Components/DateRangePicker";

const StockLedger = () => {
  // const [account, setAccount] = useState({});
  const [loading, setLoading] = useState(true);
  const [stockLedger, setStockLedger] = useState([]);
  const [error, setError] = useState(false);
  const [searchPartNo, setSearchPartNo] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [openPurchase, setOpenPurchase] = useState(false);
  const [openSales, setOpenSales] = useState(false);
  const [selectedVendorCode, setSelectedVendorCode] = useState("");
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    const getLedgerData = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await warehouseService.fetchStockLedger({
          startDate,
          endDate,
        });
        setStockLedger(response?.ledger || []);
      } catch (err) {
        console.error("Failed to fetch Ledger", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    getLedgerData();
  }, [startDate, endDate]);

  const handleOpenPurchase = (vendor_code) => {
    setSelectedVendorCode(vendor_code);
    setOpenPurchase(true);
  };

  const handleOpenSales = (vendor_code) => {
    setSelectedVendorCode(vendor_code);
    setOpenSales(true);
  };

  const filteredLedger = stockLedger.filter((item) =>
    item.vendor_code
      ?.toString()
      .toLowerCase()
      .includes(searchPartNo.toLowerCase()),
  );

  const totals = filteredLedger.reduce(
    (acc, item) => {
      acc.purchase += Number(item.invoice_purchase_quantity ?? 0);
      acc.sale += Number(item.invoice_quantity ?? 0);
      return acc;
    },
    { purchase: 0, sale: 0 },
  );

  const totalsRemaining = totals.purchase - totals.sale;

  const handleDownloadExcel = () => {
    try {
      if (!stockLedger || stockLedger.length === 0) {
        alert("No data to export!");
        return;
      }

      const excelTotals = stockLedger.reduce(
        (acc, item) => {
          acc.purchase += Number(item.invoice_purchase_quantity ?? 0);
          acc.sale += Number(item.invoice_quantity ?? 0);
          return acc;
        },
        { purchase: 0, sale: 0 },
      );

      const excelRemaining = excelTotals.purchase - excelTotals.sale;

      const rows = stockLedger.map((item, index) => ({
        "S.No": index + 1,
        "Part No.": item.vendor_code ?? "",
        Description: `${item.title ?? ""}${item.brand ? ` (${item.brand})` : ""}`,
        "Purchase Qty": Number(item.invoice_purchase_quantity ?? 0),
        "Sale Qty": Number(item.invoice_quantity ?? 0),
        "Remaining Qty": Number(item.remaining_quantity ?? 0),
      }));

      rows.push({
        "S.No": "",
        "Part No.": "TOTAL",
        Description: "",
        "Purchase Qty": excelTotals.purchase,
        "Sale Qty": excelTotals.sale,
        "Remaining Qty": excelRemaining,
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);

      worksheet["!cols"] = [
        { wch: 6 },
        { wch: 18 },
        { wch: 45 },
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Ledger");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");

      saveAs(blob, `stock-ledger-${yyyy}-${mm}-${dd}.xlsx`);
    } catch (e) {
      console.error("Excel export failed:", e);
      alert("Excel export failed. Check console.");
    }
  };

  return (
    <main className="mx-10 my-2 p-6 rounded-3xl border border-gray-200 shadow-lg mb-20 bg-white">
      <div ref={topRef} />
      {loading ? (
        <Loading />
      ) : error ? (
        <p className="text-red-600 font-semibold">
          Failed to load stock ledger.
        </p>
      ) : (
        <>
          <div className="flex flex-row justify-between items-center">
            <p className="font-bold text-3xl text-secondary">Stock Ledger</p>

            <div className="flex justify-end items-center gap-3 w-[40%]">
              <button
                onClick={handleDownloadExcel}
                className="button-primary !text-xs !w-[40%]"
              >
                Download Excel
              </button>

              <input
                type="text"
                placeholder="Search by Part Number..."
                value={searchPartNo}
                onChange={(e) => setSearchPartNo(e.target.value)}
                className="primary-input"
              />
            </div>
          </div>
          <div className="fixed bottom-10 right-10 flex flex-col gap-3 z-50">
            <button
              onClick={() =>
                topRef.current?.scrollIntoView({ behavior: "smooth" })
              }
              className="w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-lg hover:shadow-xl hover:bg-gray-50 hover:-translate-y-1 transition-all duration-300 group"
              title="Scroll to Top"
            >
              <MdKeyboardDoubleArrowUp className="text-xl text-gray-600 group-hover:text-gray-900 transition-colors duration-300" />
            </button>
            <button
              onClick={() =>
                bottomRef.current?.scrollIntoView({ behavior: "smooth" })
              }
              className="w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-lg hover:shadow-xl hover:bg-gray-50 hover:translate-y-1 transition-all duration-300 group"
              title="Scroll to Bottom"
            >
              <MdKeyboardDoubleArrowDown className="text-xl text-gray-600 group-hover:text-gray-900 transition-colors duration-300" />
            </button>
          </div>
          <div className="mt-10">
            <DateRangePicker
              onRangeChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </div>
          <div className="table-div">
            <table>
              <thead className="border-y-2 text-left w-full text-black text-[10px]">
                <tr className="uppercase w-full text-center">
                  <th className="py-4 w-[1%]">S.No</th>
                  <th className="py-4 w-[2%] text-left">Part No.</th>
                  <th className="py-4 w-[6%] text-left">Description</th>
                  <th className="py-4 w-[2%] text-center">Purchase Qty</th>
                  <th className="py-4 w-[2%] text-center">Sale Qty</th>
                  <th className="w-[2%] py-4">Remaining Qty</th>
                </tr>
              </thead>

              <tbody>
                {filteredLedger.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-gray-500">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  <>
                    {filteredLedger.map((entry, index) => (
                      <tr
                        key={`${entry.product}-${entry.vendor_code}-${index}`}
                        className="text-center border-b"
                      >
                        <td className="py-3 font-bold text-blue-500">
                          {index + 1}.
                        </td>
                        <td className="py-3 text-left font-semibold">
                          {entry.vendor_code}
                        </td>
                        <td className="py-3 text-left">
                          {entry.title} {entry.brand ? `(${entry.brand})` : ""}
                        </td>

                        <td className="py-3 text-center">
                          <button
                            onClick={() =>
                              handleOpenPurchase(entry.vendor_code)
                            }
                            className="bg-white-200 hover:shadow-md duration-300 py-1 w-10 rounded border border-gray-200 font-semibold text-gray-600"
                          >
                            {entry.invoice_purchase_quantity ?? 0}
                          </button>
                        </td>

                        <td className="py-3 text-center">
                          <button
                            onClick={() => handleOpenSales(entry.vendor_code)}
                            className="bg-white-200 hover:shadow-md duration-300 py-1 w-10 rounded border border-gray-200 font-semibold text-gray-600"
                          >
                            {entry.invoice_quantity ?? 0}
                          </button>
                        </td>

                        <td className="py-3">
                          {entry.remaining_quantity ?? 0}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
            <div className="flex flex-row justify-center gap-4 mt-10 flex-wrap">
              {[
                {
                  label: "Total Purchase Quantity",
                  value: totals.purchase,
                },
                { label: "Total Sale Quantity", value: totals.sale },
                {
                  label: "Total Remaining Qunatity",
                  value: totalsRemaining,
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="flex flex-col bg-gray-50 border border-gray-200 rounded-xl px-5 py-3"
                >
                  <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">
                    {stat.label}
                  </span>
                  <span className="text-sm font-bold text-gray-800 mt-1">
                    {stat.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <PurchaseDetailsModal
        isOpen={openPurchase}
        vendorCode={selectedVendorCode}
        onClose={() => setOpenPurchase(false)}
      />

      <SalesDetailsModal
        isOpen={openSales}
        vendorCode={selectedVendorCode}
        onClose={() => setOpenSales(false)}
      />
      <div ref={bottomRef} />
    </main>
  );
};

export default StockLedger;
